import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/utils/api";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell,
} from "recharts";
import { AlertTriangle, ArrowLeft, TrendingDown, MessageSquare, CheckCircle2, Loader } from "lucide-react";

const REASON_COLORS = {
  no_material: "#DC2626",
  tool_change: "#F97316",
  maintenance: "#F59E0B",
  quality_issue: "#8B5CF6",
  break_overrun: "#0EA5E9",
  power_cut: "#EF4444",
  operator_absent: "#64748B",
  other: "#94A3B8",
};

const DowntimeReport = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [reasonCodes, setReasonCodes] = useState([]);
  const [showPending, setShowPending] = useState(searchParams.get("pending") === "1");

  const fetchData = (d) => {
    setLoading(true);
    api.get(`/downtime/weekly-report?days=${d}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  };

  const fetchPending = () => {
    setPendingLoading(true);
    api.get("/downtime/active")
      .then((r) => setPending(r.data || []))
      .catch(() => setPending([]))
      .finally(() => setPendingLoading(false));
  };

  useEffect(() => { fetchData(days); }, [days]);
  useEffect(() => {
    fetchPending();
    api.get("/downtime/reason-codes").then((r) => setReasonCodes(r.data || [])).catch(() => {});
  }, []);

  const stackedRows = (data?.days || []).map((d) => {
    const row = { date: d.date.slice(5) };
    (data.reason_totals || []).forEach((rt) => {
      row[rt.code] = d.by_reason[rt.code] || 0;
    });
    row.__total = d.total_minutes;
    row.__events = d.total_events;
    return row;
  });

  const pieData = (data?.reason_totals || [])
    .filter((r) => r.minutes > 0)
    .map((r) => ({ name: r.label, value: r.minutes, code: r.code }));

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-7xl mx-auto p-6">
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-4"
        >
          <ArrowLeft size={14} /> Back
        </button>

        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900 inline-flex items-center gap-2">
              <TrendingDown size={22} className="text-red-600" /> Downtime Report
            </h1>
            <p className="text-sm text-slate-500">Resolved downtime events grouped by day and reason.</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold text-slate-700">Range</label>
            <select
              data-testid="downtime-range"
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              className="border-slate-300 rounded-lg text-sm px-2 py-1.5"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
            </select>
          </div>
        </div>

        {/* KPIs */}
        {data && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <KpiCard label="Total Downtime" value={`${data.grand_total_minutes} min`} color="text-red-600" />
            <KpiCard label="Events" value={data.grand_total_events} color="text-orange-600" />
            <KpiCard label="Days Covered" value={data.range?.days || 0} color="text-slate-700" />
            <KpiCard
              label="Top Reason"
              value={
                data.reason_totals
                  ?.sort((a, b) => b.minutes - a.minutes)[0]?.label || "—"
              }
              color="text-purple-700"
            />
          </div>
        )}

        {/* Pending Remarks — unresolved downtime events, grouped by machine */}
        <PendingRemarksSection
          pending={pending}
          reasonCodes={reasonCodes}
          loading={pendingLoading}
          showPending={showPending}
          setShowPending={setShowPending}
          onResolved={() => fetchPending()}
        />

        {loading ? (
          <div className="text-center text-slate-400 py-16">Loading...</div>
        ) : (
          <>
            {/* By Root Cause — primary rollup */}
            {(data?.by_root_cause || []).length > 0 && (
              <div
                className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 mb-4"
                data-testid="by-root-cause-section"
              >
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div>
                    <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500 inline-flex items-center gap-1.5">
                      <TrendingDown size={13} /> By Root Cause
                    </p>
                    <p className="text-[11px] text-slate-400">
                      Minutes rolled up by named root cause · drill into reason codes below.
                    </p>
                  </div>
                  <button
                    data-testid="manage-root-causes-btn"
                    onClick={() => navigate("/admin?tab=root-causes")}
                    className="text-[11px] font-bold text-[#1A3560] bg-white hover:bg-slate-50 border border-slate-300 px-3 py-1.5 rounded-lg"
                  >
                    Manage root causes →
                  </button>
                </div>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart
                    data={data.by_root_cause}
                    layout="vertical"
                    margin={{ top: 5, right: 20, left: 20, bottom: 5 }}
                  >
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={140} />
                    <Tooltip
                      formatter={(v, _n, p) => [`${v} min · ${p.payload.count} evt`, p.payload.name]}
                    />
                    <Bar dataKey="minutes" radius={[0, 6, 6, 0]}>
                      {data.by_root_cause.map((b) => (
                        <Cell key={b.id || "ungrouped"} fill={b.color || "#64748B"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-3 grid gap-2 sm:grid-cols-2" data-testid="root-cause-breakdown">
                  {data.by_root_cause.map((b) => (
                    <div
                      key={b.id || "ungrouped"}
                      className="border border-slate-100 rounded-lg px-3 py-2 text-xs"
                      data-testid={`rc-breakdown-${b.id || "ungrouped"}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: b.color }} />
                        <b className="text-slate-900 flex-1 truncate">{b.name}</b>
                        <span className="text-red-700 font-extrabold">{b.minutes}m</span>
                      </div>
                      <div className="flex flex-wrap gap-1 ml-4">
                        {b.reason_codes.length === 0 ? (
                          <span className="italic text-slate-400">no codes</span>
                        ) : b.reason_codes.map((code) => {
                          const match = (data?.reason_totals || []).find((r) => r.code === code);
                          return (
                            <span key={code} className="bg-slate-100 text-slate-700 text-[10px] px-1.5 py-0.5 rounded">
                              {(match?.label) || code} {match?.minutes ? `· ${match.minutes}m` : ""}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bar chart */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 mb-4">
              <p className="text-xs font-extrabold uppercase tracking-widest text-slate-400 mb-2">
                {(data?.by_root_cause || []).length > 0
                  ? "Drill-down · minutes per day (stacked by reason code)"
                  : "Downtime minutes per day (stacked by reason)"}
              </p>
              {stackedRows.length === 0 ? (
                <div className="text-center text-slate-400 py-10">No resolved downtime events in this range.</div>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={stackedRows} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} label={{ value: "minutes", angle: -90, position: "insideLeft", style: { fontSize: 11 } }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {(data?.reason_totals || []).map((rt) => (
                      <Bar
                        key={rt.code}
                        dataKey={rt.code}
                        name={rt.label}
                        stackId="a"
                        fill={REASON_COLORS[rt.code] || "#94A3B8"}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Split: pie + reason table */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
                <p className="text-xs font-extrabold uppercase tracking-widest text-slate-400 mb-2">Distribution</p>
                {pieData.length === 0 ? (
                  <div className="text-center text-slate-400 py-10">No data</div>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        outerRadius={100}
                        innerRadius={50}
                        label={(entry) => `${entry.name} (${entry.value}m)`}
                        labelLine={false}
                      >
                        {pieData.map((entry) => (
                          <Cell key={entry.code} fill={REASON_COLORS[entry.code] || "#94A3B8"} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>

              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
                <p className="text-xs font-extrabold uppercase tracking-widest text-slate-400 mb-2">Top reasons</p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-xs uppercase text-slate-400 border-b border-slate-100">
                      <th className="text-left py-1.5">Reason</th>
                      <th className="text-right py-1.5">Events</th>
                      <th className="text-right py-1.5">Minutes</th>
                    </tr>
                  </thead>
                  <tbody data-testid="downtime-reason-table">
                    {(data?.reason_totals || [])
                      .filter((r) => r.count > 0)
                      .sort((a, b) => b.minutes - a.minutes)
                      .map((r) => (
                        <tr key={r.code} className="border-b border-slate-50 hover:bg-slate-50">
                          <td className="py-2">
                            <span className="inline-flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full" style={{ background: REASON_COLORS[r.code] || "#94A3B8" }} />
                              <b className="text-slate-900">{r.label}</b>
                            </span>
                          </td>
                          <td className="py-2 text-right">{r.count}</td>
                          <td className="py-2 text-right font-extrabold text-red-600">{r.minutes}</td>
                        </tr>
                      ))}
                    {(data?.reason_totals || []).filter((r) => r.count > 0).length === 0 && (
                      <tr><td colSpan={3} className="text-center text-slate-400 py-6">
                        <span className="inline-flex items-center gap-1"><AlertTriangle size={14} /> No resolved events yet</span>
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const KpiCard = ({ label, value, color }) => (
  <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
    <p className="text-xs font-bold uppercase tracking-widest text-slate-400">{label}</p>
    <p className={`text-2xl font-extrabold ${color} truncate`}>{value}</p>
  </div>
);

const fmtClock = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const PendingRemarksSection = ({ pending, reasonCodes, loading, showPending, setShowPending, onResolved }) => {
  // Group pending events by machine_code for easy scanning
  const byMachine = {};
  pending.forEach((p) => {
    (byMachine[p.machine_code] = byMachine[p.machine_code] || []).push(p);
  });
  const groups = Object.entries(byMachine)
    .map(([mc, evs]) => ({ machine_code: mc, events: evs }))
    .sort((a, b) => b.events.length - a.events.length);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden mb-4" data-testid="pending-remarks-section">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-xs font-extrabold uppercase tracking-widest text-slate-500 inline-flex items-center gap-1.5">
            <MessageSquare size={13} /> Pending Remarks
          </p>
          <p className="text-[11px] text-slate-400 mt-0.5">
            Unresolved downtime gaps across all machines — add a reason to close them out.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            data-testid="pending-count-badge"
            className={`text-xs font-extrabold px-2.5 py-1 rounded-full ${
              pending.length > 0 ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"
            }`}
          >
            {pending.length} pending
          </span>
          <button
            data-testid="toggle-pending-btn"
            onClick={() => setShowPending((v) => !v)}
            className="text-xs font-bold text-slate-700 bg-white hover:bg-slate-50 border border-slate-300 px-3 py-1.5 rounded-lg"
          >
            {showPending ? "Hide" : "Show"}
          </button>
        </div>
      </div>
      {showPending && (
        <div className="p-4">
          {loading ? (
            <div className="text-center text-slate-400 py-4 text-sm">Loading…</div>
          ) : groups.length === 0 ? (
            <div className="text-center text-emerald-700 py-6 text-sm inline-flex items-center justify-center w-full gap-2">
              <CheckCircle2 size={16} /> No pending downtime remarks — nothing to do here.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="pending-remarks-list">
              {groups.map((g) => (
                <PendingMachineGroup
                  key={g.machine_code}
                  machine_code={g.machine_code}
                  events={g.events}
                  reasonCodes={reasonCodes}
                  onResolved={onResolved}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

const PendingMachineGroup = ({ machine_code, events, reasonCodes, onResolved }) => {
  return (
    <li className="py-3" data-testid={`pending-group-${machine_code}`}>
      <p className="text-sm font-extrabold text-slate-900 mb-2 inline-flex items-center gap-2">
        {machine_code}
        <span className="text-[10px] font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
          {events.length} event{events.length > 1 ? "s" : ""}
        </span>
      </p>
      <div className="space-y-2">
        {events.map((ev) => (
          <PendingRemarkRow key={ev.event_id} event={ev} reasonCodes={reasonCodes} onResolved={onResolved} />
        ))}
      </div>
    </li>
  );
};

const PendingRemarkRow = ({ event, reasonCodes, onResolved }) => {
  const [reason, setReason] = useState("");
  const [remark, setRemark] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Default to first reason code when the list loads
  useEffect(() => {
    if (!reason && reasonCodes.length) setReason(reasonCodes[0].code);
  }, [reasonCodes, reason]);

  const submit = async () => {
    if (!remark.trim()) {
      setError("Remark is required");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await api.post(`/downtime/${event.event_id}/remark`, { reason_code: reason, remark });
      onResolved();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save remark");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="border border-slate-200 rounded-lg p-3 bg-slate-50"
      data-testid={`pending-remark-row-${event.event_id}`}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
        <div className="text-slate-700">
          <p><b>Gap started:</b> {fmtClock(event.gap_start)}</p>
          <p><b>Duration:</b> <span className="text-red-700 font-extrabold">{event.duration_mins} min</span></p>
          {event.job_id && <p className="truncate"><b>Job:</b> {event.job_id} — {event.part_name || "—"}</p>}
          {event.operator_name && <p><b>Operator:</b> {event.operator_name}</p>}
        </div>
        <div>
          <label className="block text-[11px] font-bold text-slate-600 mb-1">Reason</label>
          <select
            data-testid={`reason-select-${event.event_id}`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full border-slate-300 rounded-lg text-xs px-2 py-1.5 focus:ring-2 focus:ring-[#1A3560]"
          >
            {reasonCodes.map((r) => (
              <option key={r.code} value={r.code}>{r.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-bold text-slate-600 mb-1">Remark <span className="text-red-500">*</span></label>
          <div className="flex gap-1.5">
            <input
              data-testid={`remark-input-${event.event_id}`}
              type="text"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder="What happened?"
              className="flex-1 border-slate-300 rounded-lg text-xs px-2 py-1.5 focus:ring-2 focus:ring-[#1A3560]"
            />
            <button
              data-testid={`save-remark-${event.event_id}`}
              onClick={submit}
              disabled={saving || !remark.trim()}
              className="text-[11px] font-extrabold text-white bg-[#DC2626] hover:bg-red-700 disabled:bg-slate-300 px-3 py-1.5 rounded-lg inline-flex items-center gap-1"
            >
              {saving ? <Loader size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
              {saving ? "Saving" : "Save"}
            </button>
          </div>
          {error && <p className="text-[10px] text-red-600 mt-1">{error}</p>}
        </div>
      </div>
    </div>
  );
};

export default DowntimeReport;
